import { aspectLabel, changeVideoFormat, VIDEO_PRESETS } from "@cva/shared";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogFooter } from "../../components/ui/dialog";
import { cn } from "../../lib/utils";
import { useEditor, useEditorApi } from "../store";

/**
 * "Format ändern" of a video (9:16, 4:5, 1:1, 16:9): unlike designs, the
 * video itself changes (no copy), as one step that ⌘Z undoes and that the
 * others see live. See `changeVideoFormat` for how the content moves.
 */
export function VideoFormatDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const store = useEditorApi();
  const current = useEditor((s) => s.doc.format);
  const readOnly = useEditor((s) => s.readOnly);
  const [presetId, setPresetId] = useState<string | null>(null);
  const selected = VIDEO_PRESETS.find((preset) => preset.id === presetId) ?? null;
  const isCurrent = (preset: (typeof VIDEO_PRESETS)[number]) =>
    preset.width === current.width && preset.height === current.height;

  const apply = () => {
    if (!selected || readOnly) return;
    store.getState().update((draft) => changeVideoFormat(draft, selected));
    onOpenChange(false);
    setPresetId(null);
    toast.success(`Format geändert: ${selected.label}`, {
      description: "Rückgängig mit ⌘Z.",
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) setPresetId(null);
      }}
    >
      <DialogContent
        title="Format ändern"
        description="Das Video bekommt das neue Seitenverhältnis. Texte, Logos und Untertitel werden proportional neu verteilt, Clips füllen das Bild wie bisher, Bildaufteilungen passen sich an. Rückgängig mit ⌘Z."
        className="max-w-2xl"
      >
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {VIDEO_PRESETS.map((preset) => {
            const active = isCurrent(preset);
            const scale = 56 / Math.max(preset.width, preset.height);
            return (
              <button
                key={preset.id}
                type="button"
                disabled={active}
                onClick={() => setPresetId(preset.id)}
                className={cn(
                  "flex flex-col items-center gap-2 rounded-lg border px-2 py-3 text-center transition disabled:cursor-default",
                  presetId === preset.id
                    ? "border-brand-500 bg-brand-50 ring-2 ring-brand-500/20"
                    : active
                      ? "border-zinc-200 bg-zinc-50"
                      : "border-zinc-200 hover:border-zinc-300",
                )}
              >
                <span className="flex h-14 items-center justify-center">
                  <span
                    className={cn("block rounded-[3px]", active ? "bg-zinc-300" : "bg-zinc-800")}
                    style={{ width: preset.width * scale, height: preset.height * scale }}
                  />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-zinc-900">
                    {aspectLabel(preset.width, preset.height)}
                  </span>
                  <span className="block text-xs leading-snug text-zinc-600">{preset.label}</span>
                  <span className="block text-[11px] text-zinc-400">
                    {active ? "Aktuelles Format" : `${preset.width} × ${preset.height} px`}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button disabled={!selected || readOnly} onClick={apply}>
            Format ändern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
